import React, { useEffect, useState, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import "./CourseUpdation.css";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { BASE_API_URL } from "../Constant";
import { getPermissionsData } from "../Redux/UserMaintenance.js";
import { getCourseData } from "../Redux/FlipbookData.js";
import FlipBook from "./TechnologyComponents/FlipBook";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Alert,
} from "@mui/material";

function CourseUpdation() {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const dispatch = useDispatch();

  const [course, setCourse] = useState("");
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [courseData, setCourseData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [backupAvailable, setBackupAvailable] = useState(false);

  const fileInputRef = useRef();

  const { permissionsData } = useSelector((state) => state.userMaintenance);
  const bookData = useSelector((state) => state.flipbookData.flipbookData);

  useEffect(() => {
    dispatch(getPermissionsData());
  }, [dispatch]);

  useEffect(() => {
    if (course) dispatch(getCourseData(course));
  }, [course]);

  const matchingCourses =
    permissionsData
      ?.flatMap((p) => p.permissions?.split(",") || [])
      .filter(Boolean) || [];

  const uniqueCourses = [...new Set(matchingCourses)];

  const handleCourseChange = async (e) => {
    const selected = e.target.value;
    setCourse(selected);
    setFile(null);
    setCourseData(null);
    setShowPreview(false);

    if (!selected) return;

    try {
      const res = await axios.get(`${BASE_API_URL}/api/checkBackup`, {
        params: { course: selected },
        headers: { Authorization: localStorage.getItem("token") },
      });
      setBackupAvailable(res.data.backupExists);
    } catch {
      setBackupAvailable(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];

    if (
      f?.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setFile(f);
      setMessage("");
    } else {
      setMessage("Please select a valid .xlsx file");
      setMessageType("error");
    }

    fileInputRef.current.value = null;
  };

  const handlePreview = async () => {
    if (!file || !course) {
      setMessage("Select course and file");
      setMessageType("error");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("course", course);
    formData.append("operation", "Preview");

    try {
      const res = await axios.post(
        `${BASE_API_URL}/api/upload-courses`,
        formData,
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        },
      );

      const data = res.data.data;

      data.pages.forEach((p, i) => {
        p.pageNumber = i + 1;
      });

      setCourseData(data);
      setShowPreview(true);
    } catch (err) {
      setMessage("Preview failed");
      setMessageType("error");
    }
  };

  const handleSubmit = async () => {
    if (!file || !course) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("course", course);
    if (activeAccount) {
      formData.append("empId", activeAccount.idTokenClaims.enterpriseID);
    }

    try {
      const res = await axios.post(
        `${BASE_API_URL}/api/upload-courses`,
        formData,
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        },
      );

      setMessage(res.data.message);
      setMessageType("success");
      setFile(null);
      setShowPreview(false);
    } catch {
      setMessage("Upload failed");
      setMessageType("error");
    }
  };

  const handleRevert = async () => {
    try {
      const res = await axios.post(
        `${BASE_API_URL}/api/revertCourseData`,
        { course },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        },
      );

      setMessage(res.data.message);
      setMessageType("success");

      dispatch({
        type: "flipbookData/setData",
        payload: res.data.data,
      });

      setCourse("");
      setFile(null);
      setCourseData(null);
    } catch {
      setMessage("Revert failed");
      setMessageType("error");
    }
  };

  const pick = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return "";
  };

  const cleanHtml = (val) => {
    if (!val) return "";

    if (typeof val === "object" && ("p" in val || "li" in val)) {
      const paragraph = val.p ? String(val.p).trim() : "";
      const listItems = Array.isArray(val.li)
        ? val.li.map((x) => `• ${String(x).trim()}`).join("\n")
        : "";
      return [paragraph, listItems].filter(Boolean).join("\n");
    }

    if (Array.isArray(val)) {
      return val.map(cleanHtml).filter(Boolean).join("\n");
    }

    if (typeof val === "string") {
      return val
        .replace(/<li[^>]*>/gi, "• ")
        .replace(/<\/li>/gi, "\n")
        .replace(/<p[^>]*>/gi, "")
        .replace(/<\/p>/gi, "\n")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .trim();
    }

    return String(val);
  };

  const handleBaseDownload = async () => {
    if (!bookData?.pages) return;

    const excludedNames = [
      "Table of Contents",
      "Introduction",
      "Landscape",
      "Video",
      "Links",
      "Courses At Glance",
    ];

    const filteredPages = bookData.pages.filter(
      (item) => !excludedNames.includes(item?.courseName),
    );

    if (filteredPages.length === 0) {
      console.warn("No data to export");
      return;
    }

    // Group by CourseName
    const grouped = new Map();
    filteredPages.forEach((item) => {
      const courseName = item?.courseName || "Unknown";
      if (!grouped.has(courseName)) grouped.set(courseName, []);
      grouped.get(courseName).push(item);
    });

    const headers = [
      "Page",
      "CourseName",
      "Description",
      "LearningOutcomes",
      "Prerequisite",
      "Duration",
      "CareerLevel",
      "CourseLevel",
      "CourseCode",
      "Mode",
    ];

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Courses");

    worksheet.addRow(headers);

    // Add data rows with page numbers
    let rowIndex = 2;
    for (const [courseName, pages] of grouped.entries()) {
      pages.forEach((item, index) => {
        const prereqVal = pick(item, ["prerequisite", "prereq"]);

        let duration = item?.duration || "";
        let careerLevel = item?.careerLevel || item?.career_level || "";
        let courseLevel = item?.courseLevel || item?.course_level || "";
        let courseCode = item?.courseCode || item?.course_code || "";
        let mode = item?.mode || "";
        let prerequisite = cleanHtml(prereqVal);

        if (typeof duration === "string" && duration.includes("duration_img2"))
          duration = "";
        if (
          typeof careerLevel === "string" &&
          careerLevel.includes("career_img2")
        )
          careerLevel = "";
        if (
          typeof courseLevel === "string" &&
          courseLevel.includes("course_img2")
        )
          courseLevel = "";
        if (typeof courseCode === "string" && courseCode.includes("code_img2"))
          courseCode = "";
        if (typeof mode === "string" && mode.includes("mode_img2")) mode = "";
        if (
          typeof prerequisite === "string" &&
          prerequisite.includes("pre_img2")
        )
          prerequisite = "";

        const rowValues = {
          Page: index + 1,
          CourseName: courseName,
          Description: cleanHtml(pick(item, ["description", "desc"])),
          LearningOutcomes: cleanHtml(
            pick(item, ["learningOutcomes", "learning_outcomes"]),
          ),
          Prerequisite: prerequisite,
          Duration: duration,
          CareerLevel: careerLevel,
          CourseLevel: courseLevel,
          CourseCode: courseCode,
          Mode: mode,
        };

        const row = worksheet.addRow(headers.map((h) => rowValues[h] || ""));

        // Bold top border if this is the first row of a new course group
        if (index === 0) {
          row.eachCell((cell) => {
            cell.border = {
              top: { style: "medium" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        }

        rowIndex++;
      });
    }

    // Style all cells: wrap, top and left aligned, borders
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      row.eachCell((cell) => {
        if (!cell.border?.top || cell.border?.top.style !== "medium") {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }

        cell.alignment = {
          vertical: "top",
          horizontal: "left",
          wrapText: true,
        };
      });
    });

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4F81BD" },
      };
      cell.alignment = {
        vertical: "top",
        horizontal: "left",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Auto-size columns with max width
    worksheet.columns.forEach((column) => {
      let maxLength = 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const text = String(cell.value || "");
        maxLength = Math.max(maxLength, text.length);
      });
      column.width = Math.min(maxLength + 5, 80);
    });

    // Freeze header row
    worksheet.views = [{ state: "frozen", ySplit: 1 }];

    // Generate file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Generate filename
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10);
    const timeStr = now
      .toLocaleTimeString("en-US", { hour12: true })
      .replace(/:/g, "-")
      .replace(/\s?(AM|PM)/, (_, meridiem) => meridiem); // remove space before AM/PM

    saveAs(blob, `${course}BaseFile_${dateStr}_${timeStr}.xlsx`);
  };

  return (
    <div className="course-container">
      <Typography variant="h4" className="admin-header">
        Admin Panel - Course Updation
      </Typography>

      <div className="admin-card">
        <div className="top-row">
          <FormControl fullWidth>
            <InputLabel>Course</InputLabel>
            <Select value={course} onChange={handleCourseChange} label="Course">
              {uniqueCourses.map((option) =>
                option ? (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ) : null,
              )}
            </Select>
          </FormControl>

          <div className="top-actions">
            <Button
              variant="outlined"
              onClick={handleBaseDownload}
              disabled={!course}
            >
              Base File
            </Button>

            <a href="Assets/ECatalogue-Flipbook-Template.xlsx" download>
              <Button variant="contained">Template</Button>
            </a>

            <Button
              variant="contained"
              color="error"
              onClick={handleRevert}
              disabled={!course || !backupAvailable}
            >
              Revert
            </Button>
          </div>
        </div>

        <div className="upload-section">
          <Button variant="outlined" component="label" disabled={!course}>
            Upload Excel
            <input
              ref={fileInputRef}
              hidden
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
            />
          </Button>

          {file && <Typography className="file-name">{file.name}</Typography>}
        </div>

        <div className="action-buttons">
          <Button variant="contained" onClick={handlePreview} disabled={!file}>
            Preview
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={!file}
          >
            Submit
          </Button>
        </div>

        {/* Message */}
        {message && (
          <Alert severity={messageType} className="alert-box">
            {message}
          </Alert>
        )}
      </div>

      {showPreview && (
        <div className="preview-container">
          <div className="preview-header">
            <Typography variant="h6">{course} FlipBook Preview</Typography>
            <Button variant="outlined" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </div>

          <FlipBook
            key={course + (file?.name || "")}
            Json={courseData}
            CourseName={course}
          />
        </div>
      )}

      <div className="instructions-card">
        <Typography className="instructions-header">Instructions</Typography>
        <ol>
          <li className="instructions-item">
            Do not modify column names in the template.
          </li>
          <li className="instructions-item">
            All operations can be done in one upload.
          </li>
          <li className="instructions-item">
            "CourseRename" → ignore for add.
          </li>
          <li className="instructions-item">
            "PrevCourseName" → ignore for update.
          </li>
          <li className="instructions-item">
            If "PrevCourseName" is empty → course goes to top.
          </li>
          <li className="instructions-item">Course name is mandatory.</li>
          <li className="instructions-item">
            Refer{" "}
            <a href="#" download>
              Job Aid
            </a>{" "}
            for details.
          </li>
        </ol>
      </div>
    </div>
  );
}

export default CourseUpdation;
